import React, { useEffect, useState } from 'react'
import { Card, Form, Modal } from 'react-bootstrap'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components';
import { BooksitoutServer } from '../../../../config/BooksitoutServer';
import LibraryMembershipCardLoading from '../LibraryMembershipCardLoading';
import LibraryMembershipCard from '../LibraryMembershipCard';
import LibraryMembershipAddFormImageTypeSearchCard from './LibraryMembershipAddFormImageTypeSearchCard';
import RowSpacer from '../../../../common/styles/RowSpacer';
import IndexContentContainer from '../../../index/IndexContentContainer';
import BottomCTA from '../../../../common/button/BottomCTA';
import useOwnerNamePrefill from '../useOwnerNamePrefill';

const LibraryMembershipAddImageModal = ({ image, membership, setMembership, show, onHide }) => {
    const navigate = useNavigate()

    const [ownerName, setOwnerName] = useState<string>('')
    const [isOwnerNameEdited, setIsOwnerNameEdited] = useState<boolean>(false)
    const ownerNamePrefill = useOwnerNamePrefill()
    useEffect(() => {
        // Don't overwrite what the user typed while it was loading
        if (ownerNamePrefill && !isOwnerNameEdited) setOwnerName(ownerNamePrefill)
    }, [ownerNamePrefill]) // eslint-disable-line react-hooks/exhaustive-deps

    const addMembership = () => {
        toast.loading('회원증을 추가하고 있어요')

        const membershipToAdd = {
            number: membership?.number,
            typeId: membership?.typeId,
            ownerName: ownerName.trim(),
            memo: '',
        }

        BooksitoutServer
            .post('/v1/library/membership', membershipToAdd)
            .then(() => {
                toast.success('회원증을 추가했어요')
                navigate('/library/membership')
            })
            .catch(() => {
                toast.error('오류가 났어요. 잠시 후 다시 시도해 주세요')
            })
    }

    return (
        <StyledModal show={show} onHide={onHide} centered fullscreen="md-down pt-5" size="xl" backdrop={true}>
            <StyledHeader closeButton className="text-center">
                <Title>인식된 회원증 수정하기</Title>
            </StyledHeader>

            <StyledBody>
                <Row>
                    <ImageContainer>
                        <Image src={image} alt="" />
                    </ImageContainer>

                    <RecognizedResultContainer>
                        {image == null ? (
                            <></>
                        ) : membership == null ? (
                            <LibraryMembershipCardLoading />
                        ) : (
                            <div className="not-clickable">
                                <LibraryMembershipCard membership={membership} />
                            </div>
                        )}

                        <RowSpacer size={5}/>

                        <IndexContentContainer>
                            {image == null ? (
                                <></>
                            ) : membership == null ? (
                                <Card style={{ minHeight: '440px' }}></Card>
                            ) : (
                                <LibraryMembershipAddFormImageEditCard
                                    membership={membership}
                                    setMembership={setMembership}
                                    ownerName={ownerName}
                                    setOwnerName={(name: string) => {
                                        setIsOwnerNameEdited(true)
                                        setOwnerName(name)
                                    }}
                                />
                            )}
                        </IndexContentContainer>
                    </RecognizedResultContainer>

                    <BottomCTA message={'회원증 추가하기'} onClick={() => addMembership()} />
                </Row>
            </StyledBody>
        </StyledModal>
    )
}

const LibraryMembershipAddFormImageEditCard = ({ membership, setMembership, ownerName, setOwnerName }) => {
	const selectType = (typeId) => {
		setMembership({
			...membership,
			typeId: typeId,
		})
	}

	return (
		<Card style={{ minHeight: '440px' }} className="mt-3 w-100">
			<Card.Body>
				<Form>
					<div className="d-flex align-items-center">
						<Form.Label>회원증 번호</Form.Label>
						<h6 className="ms-3 text-book">{membership?.number.length}</h6>
					</div>
					<Form.Control
						defaultValue={membership?.number ?? ''}
						autoComplete="off"
						autoCorrect="off"
						autoCapitalize="off"
						onChange={e => setMembership({ ...membership, number: e.target.value })}
					/>

					<div className="mt-3" />

					<Form.Label>종류</Form.Label>
					<div style={{ minHeight: '200px' }}>
						<LibraryMembershipAddFormImageTypeSearchCard
							recognizedMembership={membership}
							selectType={selectType}
						/>
					</div>

					<div className="mt-3" />

					<Form.Label>소유자 (선택)</Form.Label>
					<Form.Control
						value={ownerName}
						placeholder="회원증 소유자 이름"
						maxLength={20}
						autoComplete="off"
						onChange={e => setOwnerName(e.target.value)}
					/>
				</Form>
			</Card.Body>
		</Card>
	)
}

const Image = styled.img.attrs({
    className: 'rounded border img-fluid',
})`
	max-height: 600px;

	@media screen and (max-width: 680px) {
		max-height: 500px;
	}
`

const StyledModal = styled(Modal)`
    .modal-content {
        border: none;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    }

    @media (max-width: 768px) {
        .modal-content {
            border-radius: 0;
        }
    }
`

const StyledHeader = styled(Modal.Header)`
    background-color: var(--card-bg-color);
    color: var(--text-color);
    border-bottom: none;
    padding: 28px 24px 0 24px;

    .btn-close {
        opacity: 0.5;
        &:hover {
            opacity: 1;
        }
    }
`

const StyledBody = styled(Modal.Body)`
    background-color: var(--card-bg-color);
    color: var(--text-color);
    padding: 24px;
`

const Title = styled.h3`
    width: 100%;
    font-size: 20px;
    font-weight: 700;
`;

const Row = styled.div.attrs({
    className: 'row'
})`
    justify-content: center;
    align-items: center;
    overflow-x: hidden;
`;

const ImageContainer = styled.div.attrs({
    className: 'col-12 col-md-4 text-center d-none d-md-block'
})`
`;

const RecognizedResultContainer = styled.div.attrs({
    className: 'col-12 col-md-8 mt-4 mt-md-0'
})`
`;

export default LibraryMembershipAddImageModal